/**
 * ToneProfileDialog — persoonlijk tone-of-voice profiel van de gebruiker.
 * Frits bouwt dit profiel automatisch uit verzonden mail; hier kun je het lezen,
 * aanscherpen of vervangen door een eigen analyse. Onder dit profiel ligt de
 * bedrijfsbrede Full Frame-huisstijl (Admin → Instellingen → Mail-assistent).
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, TextField, CircularProgress, Alert, Chip, Stack,
} from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from './demoAdapter'; // demo-modus: niets naar de server
import { bevestig } from '../../utils/bevestig';

export default function ToneProfileDialog({ open, onClose }) {
  const [tekst, setTekst] = useState('');
  const [origineel, setOrigineel] = useState('');
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  const laad = async () => {
    setLoading(true); setError(null); setMsg(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/profile`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || 'Profiel laden mislukt');
      setTekst(d.tone_profile || ''); setOrigineel(d.tone_profile || ''); setMeta(d);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) laad(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const bewaar = async () => {
    setBusy(true); setError(null); setMsg(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/profile`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tone_profile: tekst }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || 'Opslaan mislukt');
      setOrigineel(d.tone_profile || ''); setMeta(d);
      setMsg(tekst.trim() ? 'Opgeslagen. Frits schrijft vanaf nu in deze stijl.' : 'Profiel gewist. Frits analyseert je verzonden mail opnieuw bij de volgende run.');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const herbouw = async () => {
    const ok = await bevestig({
      titel: 'Opnieuw analyseren?',
      tekst: 'Frits leest je laatste 150 verzonden mails en vervangt de tekst hieronder door een nieuwe analyse. Eigen aanpassingen gaan verloren.',
      knop: 'Analyseer opnieuw',
    });
    if (!ok) return;
    setBusy(true); setError(null); setMsg(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/profile/rebuild`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || 'Analyse mislukt');
      setTekst(d.tone_profile || ''); setOrigineel(d.tone_profile || ''); setMeta((m) => ({ ...(m || {}), ...d }));
      setMsg(`Nieuwe analyse op basis van ${d.sample_count || 0} verzonden mails.`);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const gewijzigd = tekst !== origineel;
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        🗣️ Mijn schrijfstijl
        {meta?.sample_count > 0 && <Chip size="small" label={`${meta.sample_count} mails geanalyseerd`} sx={{ ml: 1, height: 20, fontSize: 11 }} />}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Zo schrijft Frits namens jou. Pas de tekst aan of plak een eigen analyse. De Full Frame-huisstijl
          (kort, direct, intern "Yo", extern "Dag") ligt hieronder en geldt voor iedereen; jouw profiel gaat vóór bij verschil.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {msg && <Alert severity="success" sx={{ mb: 1.5 }}>{msg}</Alert>}
        {loading ? <CircularProgress size={22} /> : (
          <TextField
            fullWidth multiline minRows={12} maxRows={26} value={tekst} onChange={(e) => setTekst(e.target.value)}
            placeholder="Nog geen profiel. Frits maakt er een bij de eerste run, of klik op 'Analyseer mijn verzonden mail'."
            inputProps={{ maxLength: 8000, style: { fontSize: 13.5, lineHeight: 1.5 } }}
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}>{tekst.length}/8000</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
          <Button size="small" onClick={herbouw} disabled={busy || loading} sx={{ textTransform: 'none' }}>Analyseer mijn verzonden mail</Button>
        </Stack>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Sluiten</Button>
        <Button variant="contained" disableElevation onClick={bewaar} disabled={busy || loading || !gewijzigd} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {busy ? 'Bezig…' : 'Opslaan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
